import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Lock, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';
import api, { setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { scorePassword } from '../utils/passwordStrength';
import OrgPicker from '../components/OrgPicker';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [orgChoice, setOrgChoice] = useState(null);

  useEffect(() => {
    if (!token) setError('Lien invalide');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (password !== confirm) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    const { common, score } = scorePassword(password);
    if (common) { setError('Ce mot de passe est trop courant — choisissez-en un autre.'); return; }
    if (score < 2) { setError('Mot de passe trop faible. Ajoutez des majuscules, chiffres ou caractères spéciaux.'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, password });
      if (data.requires_org_choice && data.challenge) {
        setOrgChoice(data);
        return;
      }
      if (data.session_token) setToken(data.session_token);
      setUser(data.user);
      setDone(true);
      setTimeout(() => navigate('/app', { replace: true }), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Réinitialisation impossible');
    } finally {
      setSubmitting(false);
    }
  };

  if (orgChoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-[420px]">
          <div className="mb-6 p-3 rounded-xl bg-[#E6FBF7] border border-[#A7F3D0] text-[#0F766E] text-[13px] font-semibold">
            Mot de passe mis à jour ✓
          </div>
          <OrgPicker choice={orgChoice} onBack={() => navigate('/login', { replace: true })} />
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-[380px] px-6">
          <div className="w-14 h-14 rounded-2xl bg-[#E6FBF7] flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-[#0F766E]" />
          </div>
          <h1 className="mt-4 text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mot de passe mis à jour</h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]">Redirection vers votre espace…</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="max-w-[380px] text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-[#EF4444]" />
          </div>
          <h1 className="mt-4 text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Lien invalide</h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]">Ce lien ne contient pas de token. Demandez-en un nouveau depuis la page de connexion.</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="mt-5 h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-[420px]">
        <div className="mb-10" data-testid="reset-brand">
          <Logo size={48} variant="full" />
        </div>
        <h1 className="text-[26px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Définir un nouveau mot de passe
        </h1>
        <p className="text-[13.5px] text-[#6B6C85] mt-2 mb-6">
          Choisissez un mot de passe d'au moins 8 caractères. Vos autres sessions seront déconnectées.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="reset-password"
              type="password"
              placeholder="Nouveau mot de passe"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          <PasswordStrengthMeter value={password} />
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="reset-password-confirm"
              type="password"
              placeholder="Confirmer le mot de passe"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          {error && (
            <div data-testid="reset-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
          )}
          <button
            data-testid="reset-submit"
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-xl brand-gradient text-white font-semibold text-[14px] flex items-center justify-center gap-2 shadow-sm hover:opacity-95 transition-opacity disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Réinitialiser mon mot de passe
          </button>
        </form>
      </div>
    </div>
  );
}
